import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { UserPlus, AlertTriangle, User, Calendar, DollarSign } from 'lucide-react';
import { RELATIONSHIP_OPTIONS } from './constants';

const DependentModal = ({ 
  open, 
  onOpenChange, 
  dependent,
  onSave,
  isEditing = false
}) => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    age: '',
    relationship: '',
    is_paying: false
  });
  
  // Sync state when modal opens or dependent changes
  useEffect(() => {
    if (open) {
      setFormData({
        name: dependent?.name || '',
        age: dependent?.age || '',
        relationship: dependent?.relationship || '',
        is_paying: dependent?.is_paying || false
      });
    }
  }, [open, dependent]);

  const handleSave = () => {
    if (!formData.name.trim()) {
      return;
    }
    onSave({
      ...formData,
      age: formData.age ? parseInt(formData.age) : null
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] !p-0">
        <DialogHeader className="p-4 sm:p-6 pb-2">
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
            <UserPlus className="w-5 h-5 text-orange-500" />
            {isEditing ? t('myTrip.dependent.editTitle') : t('myTrip.dependent.addTitle')}
          </DialogTitle>
          <DialogDescription className="text-sm">
            {t('myTrip.dependent.description')}
          </DialogDescription>
        </DialogHeader>
        
        <div className="px-4 sm:px-6 space-y-4 overflow-y-auto">
          <Alert variant="warning" className="bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <AlertDescription className="text-amber-700 dark:text-amber-300 text-sm">
              {t('myTrip.dependent.noLoginWarning')}
            </AlertDescription>
          </Alert>

          <div>
            <Label>{t('myTrip.dependent.name')} *</Label>
            <div className="relative mt-1">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('myTrip.dependent.namePlaceholder')}
                className="pl-10"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>{t('myTrip.dependent.age')}</Label>
              <div className="relative mt-1">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="number"
                  min="0"
                  max="120"
                  value={formData.age}
                  onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                  placeholder="Ex: 8"
                  className="pl-10"
                />
              </div>
            </div>
            <div>
              <Label>{t('myTrip.dependent.relationship')}</Label>
              <Select
                value={formData.relationship}
                onValueChange={(value) => setFormData({ ...formData, relationship: value })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder={t('myTrip.dependent.selectRelationship')} />
                </SelectTrigger>
                <SelectContent>
                  {RELATIONSHIP_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label[language] || option.label['pt-BR']}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <DollarSign className="w-5 h-5 text-slate-500 flex-shrink-0" />
              <div className="min-w-0">
                <p className="font-medium text-sm">{t('myTrip.dependent.isPaying')}</p>
                <p className="text-xs text-slate-500 truncate">{t('myTrip.dependent.isPayingDescription')}</p>
              </div>
            </div>
            <Switch
              checked={formData.is_paying}
              onCheckedChange={(checked) => setFormData({ ...formData, is_paying: checked })}
              className="flex-shrink-0 ml-2"
            />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 p-4 sm:p-6 pt-2 border-t">
          <Button
            variant="outline"
            className="flex-1 rounded-full order-2 sm:order-1"
            onClick={() => onOpenChange(false)}
          >
            {t('common.cancel')}
          </Button>
          <Button
            className="flex-1 rounded-full bg-orange-500 hover:bg-orange-600 order-1 sm:order-2"
            onClick={handleSave}
            disabled={!formData.name.trim()}
          >
            {isEditing ? t('common.save') : t('common.add')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DependentModal;
